// Hold-to-gather progress bar rendered in world-space above the player.
//
// The bar uses a scale.x trick: the fill Graphics is drawn once at full
// width, then scaled to `progress` each frame — no per-frame redraw or
// GC pressure. The background is a fixed dark rect drawn once.

import { Container, Graphics } from "pixi.js";
import { balance } from "./types.ts";

const W = balance.rendering.gatherBarWidthPx;
const H = balance.rendering.gatherBarHeightPx;
const OFFSET_Y = balance.rendering.gatherBarOffsetY;

export class GatherUI {
  private readonly container: Container;
  private readonly fillBar: Graphics;
  private startedAt = 0;
  private durationMs = 0;
  private active = false;

  constructor(worldContainer: Container) {
    this.container = new Container();
    this.container.visible = false;

    const bg = new Graphics()
      .rect(0, 0, W, H)
      .fill({ color: balance.rendering.gatherBarBgColor });
    this.container.addChild(bg);

    this.fillBar = new Graphics()
      .rect(0, 0, W, H)
      .fill({ color: balance.rendering.gatherBarFillColor });
    this.fillBar.scale.x = 0;
    this.container.addChild(this.fillBar);

    worldContainer.addChild(this.container);
  }

  // Called by gatherStarted handler (Step 13). Records wall-clock start
  // so progress can be computed from Date.now() without needing a ticker
  // reference and without mixing the two clock domains (Date vs perf).
  start(durationMs: number): void {
    this.durationMs = durationMs;
    this.startedAt = Date.now();
    this.active = true;
    this.fillBar.scale.x = 0;
    this.container.visible = true;
  }

  // Called by gatherCompleted and gatherCancelled handlers (Step 13).
  stop(): void {
    this.active = false;
    this.container.visible = false;
  }

  // Called every tick to track player position and advance the fill.
  // No-op when inactive so the ticker can call this unconditionally.
  update(playerX: number, playerY: number): void {
    if (!this.active) return;
    this.container.x = playerX - W / 2; // contracts:allow-literal — centering idiom
    this.container.y = playerY - OFFSET_Y;
    const elapsed = Date.now() - this.startedAt;
    this.fillBar.scale.x = Math.min(1, elapsed / this.durationMs); // contracts:allow-literal — clamp to [0,1]
  }
}
